import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Helmet } from "react-helmet";
import NavBar from "./NavBar";
import HeroSection from "./sections/HeroSection";
import FeatureSection from "./sections/FeatureSection";
import MissionSection from "./sections/MissionSection";
import HowItWorksSection from "./sections/HowItWorksSection";
import PricingSection from "./sections/PricingSection";
import { FAQSection } from "./sections/FAQSection";
import { ContactSection } from "./sections/ContactSection";
import DashboardPreviewSection from "./sections/PreviewSection";
import { Footer } from "./sections/Footer";

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { scrollYProgress } = useScroll(); // Scroll fortschritt von 0 bis 1

  // Animiert den Pfadfortschritt basierend auf dem Scrollverhalten
  const pathProgress = useTransform(scrollYProgress, [0, 1], [0, 1]);

  useEffect(() => {
    // Spinner für 0.5 Sekunden anzeigen
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="relative flex min-h-screen w-full px-2 md:px-8 flex-col overflow-y-visible overflow-x-hidden">
      <Helmet>
        <title>Reebaq – KI-gestützte Feedback-Analyse für Unternehmen</title>
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="manifest" href="/manifest.json" />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

        <meta name="theme-color" content="#ffffff" />
        <meta
          name="description"
          content="Reebaq analysiert Kundenfeedback automatisch, erkennt Muster und Verbesserungspotenziale und steigert so Kundenzufriedenheit und Wachstum."
        />
        <meta
          name="keywords"
          content="KI Feedback Tool, Feedback Analyse, Kundenfeedback, Reebaq, SaaS Feedback, Kundenmeinung auswerten"
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Reebaq – KI-gestützte Feedback-Analyse"
        />
        <meta
          property="og:description"
          content="Belohne Kundenfeedback, analysiere es automatisch und optimiere dein Business mit Reebaq."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://reebaq.com/" />
        <meta property="og:image" content="https://reebaq.com/preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Reebaq – KI Feedback Analyse" />
        <meta
          name="twitter:description"
          content="Reebaq analysiert automatisch Feedback und liefert KI-gestützte Insights – ideal für moderne Unternehmen."
        />
        <meta
          name="twitter:image"
          content="https://deine-domain.de/preview.jpg"
        />
        <html lang="de" />
      </Helmet>

      {/* Hintergrundanimation */}
      <svg
        className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none blur-md"
        viewBox="0 0 100 1000"
        preserveAspectRatio="none"
      >
        <motion.path
          d="M50,0 Q30,200 50,400 T50,800 Q70,900 50,1000"
          stroke="#FF7051"
          strokeWidth="3"
          fill="transparent"
          style={{
            pathLength: pathProgress,
          }}
        />
      </svg>

      {/* Inhalt */}
      <div className="relative z-10">
        <NavBar />
        <main className="flex-1 flex flex-col gap-8">
          <HeroSection />
          <FeatureSection />
          <DashboardPreviewSection />
          <MissionSection />
          <HowItWorksSection />
          <PricingSection />
          <FAQSection />
          <ContactSection />
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Home;
